import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RoomList } from './RoomList';
import { CreateRoomModal } from '../modals/CreateRoomModal';
import { UserSettingsModal } from '../modals/UserSettingsModal';
import { PlayerAvatar } from '../common/PlayerAvatar';
import { useAppContext } from '@/hooks/useAppContext';
import type { CreateRoomData } from '@/types/room.types';
import type { UpdateProfileData } from '@/types/auth.types';
import { PlatformRole } from 'minka-shared';
import { 
  FaCog, 
  FaShieldAlt,
  FaSignOutAlt, 
  FaExclamationTriangle,
  FaTimes
} from 'react-icons/fa';
import { Button } from '../common';

interface RoomSelectionProps {
  onRoomSelected?: () => void;
}

export const RoomSelection: React.FC<RoomSelectionProps> = ({ onRoomSelected }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const appContext = useAppContext();
  const { auth, room, platformAdmin, notice } = appContext;
  
  // Use ref to access fresh context state at runtime
  const contextRef = useRef(appContext);
  contextRef.current = appContext;
  const { logout, user, updateProfile } = auth;
  const { 
    rooms, 
    isLoading, 
    error, 
    loadRooms, 
    createRoom, 
    deleteRoom,
    clearError 
  } = room;

  // Load rooms on component mount
  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const handleCreateRoom = async (data: CreateRoomData) => {
    await createRoom(data);
    // createRoom already reloads the rooms list
  };

  const handleJoinRoom = useCallback(async (roomId: string) => {
    try {
      
      // Use fresh context reference for accurate state
      const currentContext = contextRef.current;
      
      
      // React controls everything - WebSocket connection AND Phaser initialization
      await currentContext.roomConnection.joinRoom(roomId);
      
      // Clear any persisted chat state for fresh room join
      localStorage.removeItem(`chat_state_${roomId}`);

      // Whatever sent the user back here has been superseded by them going
      // somewhere; the banner should not be waiting when they return.
      contextRef.current.notice.clearNotice();

      onRoomSelected?.();
    } catch (error) {
      console.error('❌ RoomSelection: Failed to join room:', error);
      throw error;
    }
  }, [user, onRoomSelected]); // Removed context dependencies to prevent stale closures

  const handleDeleteRoom = async (roomId: string) => {
    await deleteRoom(roomId);
    // deleteRoom already reloads the rooms list
  };

  const handleUpdateProfile = (data: UpdateProfileData) => updateProfile(data);

  const handleLogout = () => {
    logout();
  };

  // Clear error when component unmounts or when user takes action
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-game-navy via-game-dark to-game-medium text-gray-100 relative overflow-auto">
      {/* Header Section */}
      <div className="sticky top-0 z-40 bg-gradient-to-b from-game-dark via-game-dark/95 to-game-dark/90 backdrop-blur-md border-b border-game-medium/30 shadow-lg shadow-game-medium/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Brand */}
            <div className="flex items-center">
              <img 
                src="/assets/logo/minka-logo-color.png" 
                alt="Minka" 
                className="h-12 w-auto object-contain"
              />
            </div>

            {/* User Profile Section */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <PlayerAvatar 
                  username={user?.username || 'User'} 
                  avatar={user?.avatar}
                  size="md"
                />
                <div className="hidden sm:block">
                  <div className="text-sm font-semibold text-white">{user?.username}</div>
                  <div className="text-xs text-gray-400">{user?.email}</div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {/* The console is a superadmin's tool, so an ordinary user is
                    never shown the door to it (§8). The view refuses anyway. */}
                {user?.platformRole === PlatformRole.SUPERADMIN && (
                  <Button
                    variant="secondary"
                    // Called with no argument on purpose: there is no island to
                    // leave from here, so the console is ready at once.
                    onClick={() => platformAdmin.open()}
                    title="Platform admin"
                    className="px-3 h-9 text-sm"
                  >
                    <FaShieldAlt className="text-sm" />
                    <span className="hidden md:inline font-medium">Platform admin</span>
                  </Button>
                )}

                <Button
                  variant="secondary"
                  onClick={() => setShowSettingsModal(true)}
                  disabled={isLoading}
                  title="User Settings"
                  className="px-3 h-9 text-sm"
                >
                  <FaCog className="text-sm" />
                  <span className="hidden md:inline font-medium">Settings</span>
                </Button>
                
                <Button
                  variant="danger"
                  onClick={handleLogout}
                  title="Logout"
                  className="px-3 h-9 text-sm"
                >
                  <FaSignOutAlt className="text-sm" />
                  <span className="hidden md:inline font-medium">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Why the user is looking at this screen rather than at an island:
            removed, expired, or the island itself deleted (§6.6). It is set as
            the island goes away, so this is the first screen that can say it. */}
        {notice.notice && (
          <div className="mb-6 p-4 bg-game-orange/90 backdrop-blur-sm border border-game-orange/50 text-white rounded-lg shadow-lg animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
              <span className="font-medium flex-1">{notice.notice}</span>
              <button
                type="button"
                onClick={notice.clearNotice}
                aria-label="Dismiss notice"
                className="p-1 text-white/80 hover:text-white transition-colors duration-150"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {error && (
          <div className="mb-6 p-4 bg-game-orange/90 backdrop-blur-sm border border-game-orange/50 text-white rounded-lg shadow-lg animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Room List Section */}
        <div className="mb-8">
          <RoomList
            rooms={rooms}
            onJoinRoom={handleJoinRoom}
            onDeleteRoom={handleDeleteRoom}
            onCreateRoom={() => setShowCreateModal(true)}
            onRoomUpdated={loadRooms}
            isLoading={isLoading}
          />
        </div>


      </div>

      {/* Modals */}
      <CreateRoomModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateRoom}
        isLoading={isLoading}
      />

      {user && (
        <UserSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          user={user}
          onUpdateProfile={handleUpdateProfile}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};